import { useGrowthBook } from '@growthbook/growthbook-react';
import type { FeatureDefinition } from '@growthbook/growthbook-react';
import { useCallback, useMemo } from 'react';
import Cookies from 'js-cookie';
import pick from 'lodash/pick';

import { SignupChallengeStep } from '@jerry-serverless/enums/user_challenge';

import type { ABTest } from '../types';
import type { AppData } from '../../../types/app_data';
import { isUserEligibleToRedirectToWebApp } from '../../webapp';

/**
 * Represents the value that will be saved in the GrowthBook
 * Align with mobile2
 */
export enum Variations {
  Control = 0,
  A = 1,
  B = 2,
  C = 3,
  D = 4,
  E = 5,
  Control2 = 10,
}

export const ABTestGasBuddy: ABTest = {
  storageKey: 'gasbuddy',
  eventName: 'ABTestGasBuddy',
};

const CreditRange: ABTest = {
  storageKey: 'credit_range_v1',
  eventName: 'ABTestCreditRangeV1',
};

const ConfirmDob: ABTest = {
  storageKey: 'confirm_dob',
  eventName: 'ABTestConfirmDob',
};

const AskVehicleOwnership: ABTest = {
  storageKey: 'ask_vehicle_ownership',
  eventName: 'ABTestAskVehicleOwnership',
};

const StashInterstital: ABTest = {
  storageKey: 'stash_interstitial',
  eventName: 'ABTestStashInterstitial',
};

const ABTestHomeOwnerShipPrompt: ABTest = {
  storageKey: 'home_owner_ship_prompt',
  eventName: 'HOME_OWNER_SHIP_PROMPT',
};

export const ABTestAskFullCoverageV2: ABTest = {
  storageKey: 'ask_full_coverage_v2',
  eventName: 'ABTestAskFullCoverageV2',
};

export const ABTestDefaultMileage: ABTest = {
  storageKey: 'default_mileage',
  eventName: 'ABTestDefaultMileage',
};

export const ABTestSignupRefiCrossSellCopyV2: ABTest = {
  storageKey: 'signup_refi_cross_sell_copy_v2',
  eventName: 'ABTestSignupRefiCrossSellCopyV2',
};

export const ABTestOriginationFee: ABTest = {
  storageKey: 'origination_fee_v2',
  eventName: 'ABTestOriginationFee',
};

export const ABTestGarageOnlyInsurance: ABTest = {
  storageKey: 'garage_only_insurance',
  eventName: 'ABTestGarageOnlyInsurance',
};

export const ABTestAskFullV3: ABTest = {
  storageKey: 'ask_full_coverage_v3',
  eventName: 'ABTestAskFullV3',
};

export const ABTestDriverNameVariation: ABTest = {
  storageKey: 'driver_name_variation',
  eventName: 'ABTestDriverName',
};

export const ABTestTestimonialV2: ABTest = {
  storageKey: 'testimonial_v2',
  eventName: 'ABTestTestimonialV2',
};

export const ABTestFuzzyMatching: ABTest = {
  storageKey: 'web_signup_fuzzy_matching',
  eventName: 'ABTestFuzzyMatching',
};

export const ABTestExistingUserWebapp: ABTest = {
  storageKey: 'signup_existing_user_webapp_2',
  eventName: 'ABTestExistingUserWebapp',
};

export const ABTestExistingUserWebappMobile: ABTest = {
  storageKey: 'signup_existing_user_webapp_mobile_2',
  eventName: 'ABTestExistingUserWebappMobile',
};

export const ABTestRedirectWebAppLoggedIn: ABTest = {
  storageKey: 'signup_redirect_web_app_logged_in',
  eventName: 'ABTestRedirectWebAppLoggedIn',
};

export const ABTestWebappNewUserOnboarding: ABTest = {
  storageKey: 'webapp_new_user_onboarding_2',
  eventName: 'ABTestWebappNewUserOnboarding',
};

export const ABTestWebappNewUserOnboardingLte50: ABTest = {
  storageKey: 'webapp_new_user_onboarding_lte50_2',
  eventName: 'ABTestWebappNewUserOnboardingLte50',
};

export const ABTestWebTranslate: ABTest = {
  storageKey: 'web_translate',
  eventName: 'ABTestWebTranslate',
};

export const ABTestNamePhone: ABTest = {
  storageKey: 'signup_name_phone',
  eventName: 'ABTestNamePhone',
};

export const ABTestPhoneWhyJerry: ABTest = {
  storageKey: 'signup_phone_why_jerry',
  eventName: 'ABTestPhoneWhyJerry',
};

export const ABTestSignupFinishV3: ABTest = {
  storageKey: 'signup_finish_v3',
  eventName: 'ABTestSignupFinishV3',
};

export const ABTestAdviseCoverage: ABTest = {
  storageKey: 'advise_coverage_v2',
  eventName: 'ABTestAdviseCoverage',
};

export const ABTestSignupHomeQuotingCA: ABTest = {
  storageKey: 'home_quoting_ca',
  eventName: 'ABTestSignupHomeQuotingCA',
};

export const ABTestHomePromptEnabled: ABTest = {
  storageKey: 'web_home_prompt_v2',
  eventName: 'ABTestHomePromptEnabled',
};

export const ABTestSignupBundlePrice: ABTest = {
  storageKey: 'signup_bundle_price',
  eventName: 'ABTestSignupBundlePrice',
};

export const ABTestReshoppingAlert: ABTest = {
  storageKey: 'reshopping_alert',
  eventName: 'ABTestReshoppingAlert',
};

export const ABTestWelcomeIndex3: ABTest = {
  storageKey: 'welcome_index3',
  eventName: 'ABTestWelcomeIndex3',
};

export const ABTestEmailValidationAlgorithm: ABTest = {
  storageKey: 'web_signup_email_validation_algorithm',
  eventName: 'ABTestEmailValidationAlgorithm',
};

export const ABTestCarValueNamePageSubtitleFunnel: ABTest = {
  storageKey: 'car_value_name_page_subtitle_funnel',
  eventName: 'ABTestCarValueNamePageSubtitleFunnel',
};

export const ABTestShowMilitaryStatus: ABTest = {
  storageKey: 'show_military_status',
  eventName: 'ABTestShowMilitaryStatus',
};

const EnabledTestsObject = {
  ABTestGasBuddy,
  CreditRange,
  ConfirmDob,
  AskVehicleOwnership,
  StashInterstital,
  ABTestHomeOwnerShipPrompt,
  ABTestOriginationFee,
  ABTestAskFullCoverageV2,
  ABTestSignupRefiCrossSellCopy: ABTestSignupRefiCrossSellCopyV2,
  ABTestAskFullV3,
  ABTestDriverNameVariation,
  ABTestEmailValidationAlgorithm,
  ABTestTestimonialV2,
  ABTestFuzzyMatching,
  ABTestExistingUserWebapp,
  ABTestExistingUserWebappMobile,
  ABTestRedirectWebAppLoggedIn,
  ABTestWebappNewUserOnboarding,
  ABTestWebappNewUserOnboardingLte50,
  ABTestWebTranslate,
  ABTestNamePhone,
  ABTestPhoneWhyJerry,
  ABTestSignupFinishV3,
  ABTestAdviseCoverage,
  ABTestSignupHomeQuotingCA,
  ABTestHomePromptEnabled,
  ABTestGarageOnlyInsurance,
  ABTestSignupBundlePrice,
  ABTestWelcomeIndex3,
  ABTestReshoppingAlert,
  ABTestCarValueNamePageSubtitleFunnel,
  ABTestShowMilitaryStatus,
  ABTestDefaultMileage,
};

/**
 * Only return the features that are enabled in the EnabledTestsObject
 * @param featuresData
 * @returns
 */
export function filterEnabledTestsFeatures(
  featuresData?: Record<string, FeatureDefinition>
): Record<string, FeatureDefinition> | undefined {
  // Get all the storageKeys from the EnabledTestsObject
  const enabledTestsKeys = Object.values(EnabledTestsObject).map((test) => test.storageKey);
  return featuresData ? pick(featuresData, enabledTestsKeys) : featuresData;
}

/**
 * Hook to get the value of ABTestWebappNewUserOnboarding
 * TODO: Remove this after webapp_new_user_onboarding
 */
export function useGetWebappNewUserOnboardingVariant(appData: AppData): () => number {
  const growthBook = useGrowthBook();

  return useCallback(() => {
    if (growthBook && appData.dateOfBirthMasked) {
      const birthYear = parseInt(appData.dateOfBirthMasked.slice(0, 4), 10);
      if (!Number.isNaN(birthYear)) {
        const age = new Date().getFullYear() - birthYear;
        if (age > 50) {
          return growthBook.getFeatureValue(ABTestWebappNewUserOnboarding.storageKey, 0);
        } else {
          return growthBook.getFeatureValue(ABTestWebappNewUserOnboardingLte50.storageKey, 0);
        }
      }
    }
    return 0;
  }, [appData.dateOfBirthMasked, growthBook]);
}

/**
 * Hook to get the value of signup_existing_user_webapp
 * TODO: Remove this after signup_existing_user_webapp
 */
function useGetSignupExistingUserWebappVariant(appData: AppData): () => number {
  const growthBook = useGrowthBook();

  return useCallback(() => {
    if (growthBook && isUserEligibleToRedirectToWebApp(appData)) {
      if (appData.isMobileAgent) {
        return growthBook.getFeatureValue(ABTestExistingUserWebappMobile.storageKey, 0);
      }
      if (!appData.isMobileAgent && appData.signupParams?.appOS) {
        return growthBook.getFeatureValue(ABTestExistingUserWebapp.storageKey, 0);
      }
    }
    return 0;
  }, [appData, growthBook]);
}

/**
 * Hook to check if finish page should show the webapp copy
 */
export function useShouldFinishShowWebappCopy(appData: AppData): boolean {
  const getWebappNewUserOnboardingVariant = useGetWebappNewUserOnboardingVariant(appData);
  const getSignupExistingUserWebappVariant = useGetSignupExistingUserWebappVariant(appData);

  return useMemo(() => {
    if (typeof document !== 'undefined') {
      const refCode = Cookies.get('jrcod') || '';
      /**
       * If the user is referred by a user, we should not show the webapp copy
       */
      if (!!refCode && refCode.match?.(/^u.+/i) && refCode.length > 1) {
        return false;
      }
    }

    if (appData.signupChallengeStep === SignupChallengeStep.Finished) {
      return getSignupExistingUserWebappVariant() === 2;
    }
    return getWebappNewUserOnboardingVariant() === 2;
  }, [appData.signupChallengeStep, getSignupExistingUserWebappVariant, getWebappNewUserOnboardingVariant]);
}

/**
 * ABTests which send track events.
 */
export const EnabledABTests: ABTest[] = Object.values(EnabledTestsObject);

export default EnabledTestsObject;
